import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaEye, FaCheck, FaTimes, FaEnvelope, FaPhone, FaUser, FaGraduationCap } from 'react-icons/fa';
import api from '../services/api';
import { normalizeListForAdmin } from '../utils/normalizeApi';

function AdmissionsManagement() {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => { fetchAdmissions(); }, []);

  const fetchAdmissions = async () => {
    try {
      const response = await api.get('/admissions');
      console.log('🎓 Fetched admissions:', response.data);
      setAdmissions(normalizeListForAdmin(response.data));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admissions:', error);
      setAdmissions([
        { id: 1, studentName: 'Rahul Kumar', fatherName: 'Suresh Kumar', motherName: 'Meera Kumar', class: 'Class 10', phone: '9876543210', email: 'rahul@email.com', enquiry: 'Want to know about admission process', status: 'new', createdAt: new Date().toISOString() }
      ]);
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      console.log('🔄 Updating status:', id, status);
      // Map frontend status to backend ENUM values
      const statusMap = {
        'new': 'Pending',
        'read': 'Under Review',
        'followup': 'Under Review',
        'admitted': 'Approved',
        'rejected': 'Rejected'
      };
      const backendStatus = statusMap[status] || status;
      
      const response = await api.put(`/admissions/${id}`, { status: backendStatus });
      console.log('✅ Response:', response.data);
      alert(`✅ Status updated to ${backendStatus}`);
      fetchAdmissions();
    } catch (error) {
      console.error('❌ Error updating status:', error);
      console.error('Response:', error.response?.data);
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const viewDetails = (admission) => {
    setSelectedAdmission(admission);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setTimeout(() => setSelectedAdmission(null), 300);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Pending': return 'bg-blue-100 text-blue-800';
      case 'Under Review': return 'bg-yellow-100 text-yellow-800';
      case 'Approved': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-blue-900">Admission Enquiries</h1>
            <p className="text-gray-600 mt-1">Manage student admission requests</p>
          </div>
          <div className="bg-blue-50 px-6 py-3 rounded-lg border border-blue-200">
            <span className="text-sm text-blue-600">Total Enquiries: </span>
            <span className="text-2xl font-bold text-blue-900">{admissions.length}</span>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600">Pending</p>
            <p className="text-2xl font-bold text-blue-900">{admissions.filter(a => a.status === 'Pending').length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-gray-600">Under Review</p>
            <p className="text-2xl font-bold text-yellow-900">{admissions.filter(a => a.status === 'Under Review').length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-600">Approved</p>
            <p className="text-2xl font-bold text-green-900">{admissions.filter(a => a.status === 'Approved').length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
            <p className="text-sm text-gray-600">Rejected</p>
            <p className="text-2xl font-bold text-red-900">{admissions.filter(a => a.status === 'Rejected').length}</p>
          </div>
        </div>

        {/* Admissions Table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Student</th>
                  <th className="px-6 py-4 text-left font-semibold">Class</th>
                  <th className="px-6 py-4 text-left font-semibold">Contact</th>
                  <th className="px-6 py-4 text-left font-semibold">Enquiry</th>
                  <th className="px-6 py-4 text-left font-semibold">Date</th>
                  <th className="px-6 py-4 text-left font-semibold">Status</th>
                  <th className="px-6 py-4 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {admissions.map((item) => (
                  <tr key={item.id} className="hover:bg-blue-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <FaUser className="text-blue-900" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{item.studentName}</p>
                          <p className="text-sm text-gray-500">F: {item.fatherName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {item.class}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaPhone className="text-blue-900" />
                          <span>{item.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaEnvelope className="text-blue-900" />
                          <span>{item.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="text-sm text-gray-600 truncate">{item.enquiry}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(item.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                        {item.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => viewDetails(item)} 
                          className="p-2 bg-blue-50 text-blue-900 rounded hover:bg-blue-100 transition" title="View Details">
                          <FaEye size={16} />
                        </button>
                        <button onClick={() => updateStatus(item.id, 'read')} 
                          className="p-2 bg-blue-50 text-blue-900 rounded hover:bg-blue-100 transition" title="Mark as Read">
                          <FaEye size={16} />
                        </button>
                        <button onClick={() => updateStatus(item.id, 'admitted')} 
                          className="p-2 bg-green-50 text-green-900 rounded hover:bg-green-100 transition" title="Mark as Admitted">
                          <FaCheck size={16} />
                        </button>
                        <button onClick={() => updateStatus(item.id, 'rejected')} 
                          className="p-2 bg-red-50 text-red-900 rounded hover:bg-red-100 transition" title="Mark as Rejected">
                          <FaTimes size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {admissions.length === 0 && (
          <div className="text-center text-gray-600 mt-12 py-8">
            <p className="text-xl">No admission enquiries yet</p>
          </div>
        )}
      </div>

      {/* View Details Popup */}
      {showPopup && selectedAdmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closePopup}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-6 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold">{selectedAdmission.studentName}</h2>
                  <p className="text-blue-100 mt-1">Admission Enquiry Details</p>
                </div>
                <button onClick={closePopup} className="text-white hover:text-blue-200 transition p-2">
                  <FaTimes size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Personal Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <FaUser className="text-blue-900" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-6 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Student Name</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.studentName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date of Birth</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.dateOfBirth ? new Date(selectedAdmission.dateOfBirth).toLocaleDateString('en-IN') : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Gender</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.gender || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Class Applied</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.classApplied || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Parents Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <FaGraduationCap className="text-blue-900" />
                  Parents Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-6 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Father's Name</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.fatherName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Mother's Name</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.motherName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Father's Phone</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.fatherPhone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Mother's Phone</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.motherPhone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Contact & Address */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <FaEnvelope className="text-blue-900" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-6 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Email Address</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="font-semibold text-gray-800">{selectedAdmission.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              {(selectedAdmission.previousSchool || selectedAdmission.remarks) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-blue-900 mb-4">Additional Information</h3>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    {selectedAdmission.previousSchool && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-600">Previous School</p>
                        <p className="font-semibold text-gray-800">{selectedAdmission.previousSchool}</p>
                      </div>
                    )}
                    {selectedAdmission.remarks && (
                      <div>
                        <p className="text-sm text-gray-600">Remarks</p>
                        <p className="font-semibold text-gray-800">{selectedAdmission.remarks}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status & Date */}
              <div className="flex justify-between items-center bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div>
                  <p className="text-sm text-blue-600">Enquiry Date</p>
                  <p className="text-lg font-bold text-blue-900">
                    {selectedAdmission.enquiryDate ? new Date(selectedAdmission.enquiryDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Current Status</p>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(selectedAdmission.status)}`}>
                    {selectedAdmission.status?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8 pt-6 border-t">
                <button onClick={() => { updateStatus(selectedAdmission.id, 'admitted'); closePopup(); }} 
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition font-semibold">
                  Mark as Admitted
                </button>
                <button onClick={() => { updateStatus(selectedAdmission.id, 'rejected'); closePopup(); }} 
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition font-semibold">
                  Reject
                </button>
                <button onClick={closePopup} 
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition font-semibold">
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdmissionsManagement;
